import { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { createRoot } from "react-dom/client";
import AchievementNotification from "@/components/AchievementNotification";
import { readStoredSettings } from "@/lib/userSettings";
import { notifyAchievementBrowser } from "@/hooks/useBrowserNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function achievementNotificationsEnabled() {
  try {
    const settings = readStoredSettings();
    return settings.achievementNotifications !== false;
  } catch {
    return true;
  }
}

export default function useAchievementNotifications(user) {
  const location = useLocation();
  const lastCheck = useRef(0);
  const checkingRef = useRef(false);
  const currentUserIdRef = useRef(null);
  const queuedAchievementIdsRef = useRef(new Set());
  const displayedAchievementIdRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const containerRef = useRef(null);
  const rootRef = useRef(null);

  // Show next achievement in queue
  const showNext = useCallback(() => {
    if (queue.length === 0) return;

    const achievement = queue[0];
    const achievementId = achievement.achievement_id || achievement.id;
    if (!achievementId || displayedAchievementIdRef.current === achievementId) return;
    displayedAchievementIdRef.current = achievementId;
    
    // Create container if not exists
    if (!containerRef.current) {
      containerRef.current = document.createElement("div");
      containerRef.current.id = "achievement-notification-container";
      document.body.appendChild(containerRef.current);
      rootRef.current = createRoot(containerRef.current);
    }

    const handleClose = () => {
      rootRef.current?.render(null);
      displayedAchievementIdRef.current = null;
      setQueue((prev) => {
        const [current, ...remaining] = prev;
        const currentId = current?.achievement_id || current?.id;
        if (currentId) queuedAchievementIdsRef.current.delete(currentId);
        return remaining;
      });
    };

    rootRef.current.render(
      <AchievementNotification achievement={achievement} onClose={handleClose} />
    );
  }, [queue]);

  // Show achievements when queue changes
  useEffect(() => {
    if (queue.length > 0) {
      showNext();
    }
  }, [queue, showNext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        rootRef.current?.unmount();
        containerRef.current.remove();
        containerRef.current = null;
        rootRef.current = null;
      }
    };
  }, []);

  const checkUnseen = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token || !user) return;
    if (!achievementNotificationsEnabled()) return;
    if (checkingRef.current) return;

    if (currentUserIdRef.current !== user.id) {
      currentUserIdRef.current = user.id;
      queuedAchievementIdsRef.current.clear();
      displayedAchievementIdRef.current = null;
      setQueue([]);
    }

    const now = Date.now();
    if (now - lastCheck.current < 15000) return;
    lastCheck.current = now;
    checkingRef.current = true;

    try {
      const res = await axios.get(`${API}/profile/unseen-achievements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const unseen = Array.isArray(res.data) ? res.data : [];
      const newAchievements = unseen
        .filter((achievement) => {
          const id = achievement?.achievement_id || achievement?.id;
          return id && achievement?.name?.trim() && !queuedAchievementIdsRef.current.has(id);
        })
        .map((achievement) => ({ ...achievement, id: achievement.achievement_id || achievement.id }));
      if (!newAchievements.length) return;

      newAchievements.forEach((achievement) => queuedAchievementIdsRef.current.add(achievement.id));

      // Mark first so an app reload or route transition cannot replay the same popup.
      await axios.post(`${API}/profile/mark-achievements-seen`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Add to queue (will show one at a time)
      setQueue((prev) => [...prev, ...newAchievements]);
      newAchievements.forEach((achievement) => notifyAchievementBrowser(achievement));
    } catch {
      // silent
    } finally {
      checkingRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (user) checkUnseen();
  }, [location.pathname, user, checkUnseen]);
}
